import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  CheckCircle,
  Circle,
  Clock,
  AlertTriangle,
  Edit,
  Trash2,
  Save,
  X,
  ListTodo,
  UserCircle,
  ChevronRight,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useBrand } from '../contexts/BrandContext';
import { useConfirm } from '../hooks/useConfirm';
import { usePrompt } from '../hooks/usePrompt';
import { useTaskCache } from '../contexts/TaskCacheContext';
import ConfirmModal from './ConfirmModal';
import PromptModal from './PromptModal';
import { taskService, Task, TaskTemplate } from '../lib/taskService';
import { getBrandUsers } from '../lib/crmService';

interface TaskListProps {
  opportunityId: string;
  onRefresh?: () => void;
}

const TaskList: React.FC<TaskListProps> = ({ opportunityId, onRefresh }) => {
  const { selectedBrand } = useBrand();
  const { success, error } = useToast();
  const { confirm, confirmState, closeConfirm } = useConfirm();
  const { prompt, promptState, closePrompt } = usePrompt();
  const { getTasks: getCachedTasks, invalidateCache } = useTaskCache();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [brandUsers, setBrandUsers] = useState<Array<{ id: string; email: string; full_name: string; role: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    assigned_to: '',
  });
  const [parentTaskForSubtask, setParentTaskForSubtask] = useState<string | null>(null);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    loadTasks();
    loadTemplates();
    loadBrandUsers();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadTasks(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const refreshInterval = setInterval(() => {
      if (!document.hidden) {
        loadTasks(true);
      }
    }, 60000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(refreshInterval);
    };
  }, [opportunityId, selectedBrand]);

  const loadTasks = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const data = await getCachedTasks(opportunityId, forceRefresh);
      setTasks(data);
    } catch (err) {
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    if (!selectedBrand) return;

    try {
      const data = await taskService.getActiveTaskTemplates(selectedBrand.id);
      setTemplates(data);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const loadBrandUsers = async () => {
    if (!selectedBrand) return;

    try {
      const users = await getBrandUsers(selectedBrand.id);
      setBrandUsers(users);
    } catch (err) {
      console.error('Error loading brand users:', err);
    }
  };

  const handleCompleteTask = async (task: Task) => {
    const notes = await prompt({
      title: 'Complete Task',
      message: 'Add completion notes (optional):',
      placeholder: 'Enter any notes about completing this task...',
      submitLabel: 'OK',
      cancelLabel: 'Cancel'
    });

    if (notes === null) return;

    setTasks(prev => prev.map(t =>
      t.id === task.id
        ? { ...t, status: 'completed' as const, completion_notes: notes || undefined }
        : t
    ));

    try {
      await taskService.completeTask(task.id, notes || undefined);
      success('Task marked as complete');
      onRefresh?.();
    } catch (err) {
      error('Failed to complete task');
      console.error('Error completing task:', err);
      loadTasks();
    }
  };

  const handleReopenTask = async (task: Task) => {
    setTasks(prev => prev.map(t =>
      t.id === task.id ? { ...t, status: 'pending' as const } : t
    ));

    try {
      await taskService.reopenTask(task.id);
      success('Task reopened');
      onRefresh?.();
    } catch (err) {
      error('Failed to reopen task');
      console.error('Error reopening task:', err);
      loadTasks();
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const confirmed = await confirm({
      title: 'Delete Task',
      message: 'Are you sure you want to delete this task?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger'
    });

    if (!confirmed) {
      return;
    }

    setTasks(prev => prev.filter(t => t.id !== taskId && t.parent_task_id !== taskId));

    try {
      const deleted = await taskService.deleteTask(taskId);
      if (deleted) {
        success('Task deleted');
        onRefresh?.();
      } else {
        error('Failed to delete task');
        loadTasks();
      }
    } catch (err) {
      error('Failed to delete task');
      console.error('Error deleting task:', err);
      loadTasks();
    }
  };

  const handleAddFromTemplate = async (templateId: string) => {
    if (creatingTask) return;

    setCreatingTask(true);
    setShowAddMenu(false);

    const template = templates.find(t => t.id === templateId);
    const tempId = `temp-${Date.now()}`;

    if (template) {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + template.due_days_offset);

      const optimisticTask: Task = {
        id: tempId,
        opportunity_id: opportunityId,
        template_id: templateId,
        title: template.name,
        description: template.description,
        due_date: dueDate.toISOString().split('T')[0],
        priority: template.priority,
        status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        subtasks: [],
      };

      setTasks(prev => [optimisticTask, ...prev]);
    }

    try {
      const createdTask = await taskService.createTaskFromTemplate(opportunityId, templateId);

      if (createdTask) {
        setTasks(prev => prev.map(t => t.id === tempId ? createdTask : t));
        success('Task created from template');
        invalidateCache(opportunityId);
      } else {
        setTasks(prev => prev.filter(t => t.id !== tempId));
        error('Failed to create task');
      }

      onRefresh?.();
    } catch (err) {
      setTasks(prev => prev.filter(t => t.id !== tempId));
      error('Failed to create task');
      console.error('Error creating task:', err);
    } finally {
      setCreatingTask(false);
    }
  };

  const handleSubmitManualTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (creatingTask) return;

    setCreatingTask(true);

    const tempId = `temp-${Date.now()}`;
    const optimisticTask: Task = {
      id: tempId,
      opportunity_id: opportunityId,
      title: formData.title,
      description: formData.description || undefined,
      due_date: formData.due_date,
      priority: formData.priority,
      status: 'pending',
      assigned_to: formData.assigned_to || undefined,
      parent_task_id: parentTaskForSubtask || undefined,
      order_index: parentTaskForSubtask ? 0 : undefined,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      subtasks: [],
    };

    if (parentTaskForSubtask) {
      setTasks(prev => prev.map(t => {
        if (t.id === parentTaskForSubtask) {
          return {
            ...t,
            subtasks: [...(t.subtasks || []), optimisticTask],
          };
        }
        return t;
      }));
      setExpandedTasks(prev => new Set([...prev, parentTaskForSubtask]));
    } else {
      setTasks(prev => [optimisticTask, ...prev]);
    }

    try {
      const createdTask = await taskService.createTask({
        opportunity_id: opportunityId,
        title: formData.title,
        description: formData.description || undefined,
        due_date: formData.due_date,
        priority: formData.priority,
        assigned_to: formData.assigned_to || undefined,
        parent_task_id: parentTaskForSubtask || undefined,
      });

      if (createdTask) {
        if (parentTaskForSubtask) {
          setTasks(prev => prev.map(t => {
            if (t.id === parentTaskForSubtask) {
              return {
                ...t,
                subtasks: (t.subtasks || []).map(st => st.id === tempId ? createdTask : st),
              };
            }
            return t;
          }));
        } else {
          setTasks(prev => prev.map(t => t.id === tempId ? createdTask : t));
        }
        success(parentTaskForSubtask ? 'Subtask created successfully' : 'Task created successfully');
      } else {
        if (parentTaskForSubtask) {
          setTasks(prev => prev.map(t => {
            if (t.id === parentTaskForSubtask) {
              return {
                ...t,
                subtasks: (t.subtasks || []).filter(st => st.id !== tempId),
              };
            }
            return t;
          }));
        } else {
          setTasks(prev => prev.filter(t => t.id !== tempId));
        }
        error('Failed to create task');
      }

      setShowManualForm(false);
      setParentTaskForSubtask(null);
      setFormData({
        title: '',
        description: '',
        due_date: '',
        priority: 'medium',
        assigned_to: '',
      });
      onRefresh?.();
    } catch (err) {
      if (parentTaskForSubtask) {
        setTasks(prev => prev.map(t => {
          if (t.id === parentTaskForSubtask) {
            return {
              ...t,
              subtasks: (t.subtasks || []).filter(st => st.id !== tempId),
            };
          }
          return t;
        }));
      } else {
        setTasks(prev => prev.filter(t => t.id !== tempId));
      }
      error('Failed to create task');
      console.error('Error creating task:', err);
    } finally {
      setCreatingTask(false);
    }
  };

  const handleAddSubtask = (parentTaskId: string) => {
    setParentTaskForSubtask(parentTaskId);
    setShowManualForm(true);
    setShowAddMenu(false);
    setEditingTask(null);
    const parentTask = tasks.find(t => t.id === parentTaskId);
    setFormData({
      title: '',
      description: '',
      due_date: parentTask?.due_date || '',
      priority: 'medium',
      assigned_to: parentTask?.assigned_to || '',
    });
  };

  const toggleTaskExpanded = (taskId: string) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const handleUpdateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTask) return;

    try {
      await taskService.updateTask(editingTask.id, {
        title: formData.title,
        description: formData.description || undefined,
        due_date: formData.due_date,
        priority: formData.priority,
        assigned_to: formData.assigned_to || undefined,
      });

      success('Task updated successfully');
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        due_date: '',
        priority: 'medium',
        assigned_to: '',
      });
      loadTasks();
      onRefresh?.();
    } catch (err) {
      error('Failed to update task');
      console.error('Error updating task:', err);
    }
  };

  const handleEditTask = (task: Task) => {
    setShowAddMenu(false);
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      due_date: task.due_date,
      priority: task.priority,
      assigned_to: task.assigned_to || '',
    });
    setShowManualForm(true);

    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }, 100);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-blue-600 bg-blue-100';
      case 'low': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (task: Task) => {
    const today = new Date().toISOString().split('T')[0];
    const isOverdue = task.due_date < today && task.status !== 'completed';

    if (task.status === 'completed') {
      return <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />;
    }
    if (isOverdue) {
      return <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />;
    }
    if (task.status === 'in_progress') {
      return <Clock className="w-5 h-5 text-blue-600 flex-shrink-0" />;
    }
    return <Circle className="w-5 h-5 text-gray-400 flex-shrink-0" />;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isOverdue = (task: Task) => {
    const today = new Date().toISOString().split('T')[0];
    return task.due_date < today && task.status !== 'completed';
  };

  const activeTasks = tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <ListTodo className="w-5 h-5" />
          Tasks ({activeTasks.length} active)
        </h4>
        <div className="relative">
          <button
            onClick={() => setShowAddMenu(!showAddMenu)}
            disabled={creatingTask}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {creatingTask ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Add Task
              </>
            )}
          </button>

          {showAddMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowAddMenu(false)}
              />
              <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                <div className="p-2 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 px-2 py-1">From Template</p>
                </div>
                <div className="p-2 max-h-64 overflow-y-auto">
                  {templates.length === 0 ? (
                    <p className="text-xs text-gray-500 px-2 py-4 text-center">
                      No templates available
                    </p>
                  ) : (
                    templates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleAddFromTemplate(template.id)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="font-medium text-sm text-gray-900">{template.name}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Due in {template.due_days_offset} days
                        </div>
                      </button>
                    ))
                  )}
                </div>
                <div className="p-2 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowAddMenu(false);
                      setShowManualForm(true);
                      setEditingTask(null);
                      setFormData({
                        title: '',
                        description: '',
                        due_date: '',
                        priority: 'medium',
                        assigned_to: '',
                      });

                      setTimeout(() => {
                        if (formRef.current) {
                          formRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'nearest',
                          });
                        }
                      }, 100);
                    }}
                    className="w-full px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    Create Custom Task
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showManualForm && (
        <form ref={formRef} onSubmit={editingTask ? handleUpdateTask : handleSubmitManualTask} className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4 scroll-mt-4">
          <div className="flex items-center justify-between">
            <h5 className="font-semibold text-gray-900">
              {editingTask ? 'Edit Task' : (parentTaskForSubtask ? 'Create Subtask' : 'Create Custom Task')}
            </h5>
            <button
              type="button"
              onClick={() => {
                setShowManualForm(false);
                setEditingTask(null);
                setParentTaskForSubtask(null);
                setFormData({
                  title: '',
                  description: '',
                  due_date: '',
                  priority: 'medium',
                  assigned_to: '',
                });
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Task Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Send follow-up email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Task details..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date *
              </label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority *
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign To
            </label>
            <select
              value={formData.assigned_to}
              onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Unassigned</option>
              {brandUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => {
                setShowManualForm(false);
                setEditingTask(null);
              }}
              className="px-4 py-2 text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {editingTask ? 'Update' : 'Create'} Task
            </button>
          </div>
        </form>
      )}

      {loading && tasks.length === 0 ? (
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="border border-gray-200 bg-white rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 bg-gray-200 rounded-full flex-shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  <div className="flex items-center gap-4">
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                    <div className="h-3 bg-gray-200 rounded w-32"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-8 text-gray-400 border border-dashed border-gray-300 rounded-lg">
          <ListTodo className="w-12 h-12 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No tasks yet</p>
          <p className="text-xs mt-1">Add your first task to get started</p>
        </div>
      ) : (
        <div className="space-y-4">
          {activeTasks.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Active Tasks
              </h5>
              {activeTasks.map((task) => {
                const isOptimistic = task.id.startsWith('temp-');
                return (
                <div
                  key={task.id}
                  className={`border rounded-lg p-4 transition-all ${
                    isOptimistic
                      ? 'border-blue-200 bg-blue-50 animate-pulse'
                      : isOverdue(task)
                      ? 'border-red-200 bg-red-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  {isOptimistic && (
                    <div className="mb-2 flex items-center gap-2 text-xs text-blue-600">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Creating task...</span>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCompleteTask(task);
                      }}
                      className="mt-1"
                    >
                      {getStatusIcon(task)}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h6 className="font-semibold text-gray-900">{task.title}</h6>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                        {isOverdue(task) && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                            Overdue
                          </span>
                        )}
                      </div>
                      {task.description && (
                        <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Due: {formatDate(task.due_date)}</span>
                        {task.assigned_user && (
                          <span className="flex items-center gap-1">
                            <UserCircle className="w-3 h-3" />
                            {task.assigned_user.full_name}
                          </span>
                        )}
                        {task.template && (
                          <span>From template: {task.template.name}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddSubtask(task.id);
                        }}
                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Add subtask"
                      >
                        <Plus className="w-4 h-4 text-blue-600" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleEditTask(task);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Edit task"
                      >
                        <Edit className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteTask(task.id);
                        }}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete task"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                      {task.subtasks && task.subtasks.length > 0 && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleTaskExpanded(task.id);
                          }}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title={expandedTasks.has(task.id) ? "Collapse subtasks" : "Expand subtasks"}
                        >
                          {expandedTasks.has(task.id) ? (
                            <ChevronDown className="w-4 h-4 text-gray-600" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-600" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {task.subtasks && task.subtasks.length > 0 && expandedTasks.has(task.id) && (
                    <div className="mt-3 ml-8 space-y-2">
                      {task.subtasks.map((subtask) => (
                        <div
                          key={subtask.id}
                          className="border border-gray-200 bg-white rounded-lg p-3"
                        >
                          <div className="flex items-start gap-3">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCompleteTask(subtask);
                              }}
                              className="mt-1"
                            >
                              {getStatusIcon(subtask)}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h6 className="font-medium text-gray-900 text-sm">{subtask.title}</h6>
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(subtask.priority)}`}>
                                  {subtask.priority}
                                </span>
                                {isOverdue(subtask) && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                                    Overdue
                                  </span>
                                )}
                              </div>
                              {subtask.description && (
                                <p className="text-xs text-gray-600 mb-1">{subtask.description}</p>
                              )}
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span>Due: {formatDate(subtask.due_date)}</span>
                                {subtask.assigned_user && (
                                  <span className="flex items-center gap-1">
                                    <UserCircle className="w-3 h-3" />
                                    {subtask.assigned_user.full_name}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleEditTask(subtask);
                                }}
                                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Edit subtask"
                              >
                                <Edit className="w-3.5 h-3.5 text-gray-600" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDeleteTask(subtask.id);
                                }}
                                className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete subtask"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-600" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          )}

          {completedTasks.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Completed ({completedTasks.length})
              </h5>
              {completedTasks.map((task) => (
                <div
                  key={task.id}
                  className="border border-gray-200 bg-gray-50 rounded-lg p-4 opacity-75"
                >
                  <div className="flex items-start gap-3">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleReopenTask(task);
                      }}
                      className="mt-1"
                    >
                      {getStatusIcon(task)}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h6 className="font-semibold text-gray-700 line-through">{task.title}</h6>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </div>
                      {task.notes && (
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">Notes:</span> {task.notes}
                        </p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Completed: {task.completed_at ? formatDate(task.completed_at.split('T')[0]) : 'Unknown'}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteTask(task.id);
                      }}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete task"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        cancelLabel={confirmState.cancelLabel}
        variant={confirmState.variant}
        brandColor={selectedBrand?.primary_color}
      />

      <PromptModal
        isOpen={promptState.isOpen}
        onClose={closePrompt}
        onSubmit={promptState.onSubmit}
        title={promptState.title}
        message={promptState.message}
        placeholder={promptState.placeholder}
        defaultValue={promptState.defaultValue}
        submitLabel={promptState.submitLabel}
        cancelLabel={promptState.cancelLabel}
        brandColor={selectedBrand?.primary_color}
      />
    </div>
  );
};

export default TaskList;
